import Immutable from 'immutable';
import {push} from 'connected-react-router/immutable';
import {isAuthenticated, getFromLocation} from '../services/auth';
import {local} from '../services/local';
import {THEMES} from '@services/utilities';
import {fetch, buildFetch} from '../services/fetch';
import {createError} from '../services/errors';
import {
  HOME,
  LOGIN,
  NEW_PASSWORD,
  CAMPAIGNSELECTOR,
  getParametricRoute,
  PRIVACY,
  SSO_LOGOUT,
} from '../services/routes';
import {
  setUserCampaignList,
  selectUserCampaign,
  loadUserCampaignsSuccess,
  loadUserCampaignListSuccess,
} from '../actions/userCampaign';
import {getUserData} from '../reducers/user';
import config from 'config';
import {setThemeId} from './ui';
import {privacyAgreementLoadingSuccess} from './privacyAgreement';
import {loadLanguages} from './languages';

export const START_LOGIN = 'START_LOGIN';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_ERROR = 'LOGIN_ERROR';
export const INVALID_CREDENTIALS_ERROR = 'INVALID_CREDENTIALS_ERROR';
export const NO_ACTIVE_CAMPAIGN_ERROR = 'NO_ACTIVE_CAMPAIGN_ERROR';
export const AUTH_EXPIRED_ERROR = 'AUTH_ERROR';
export const START_LOGOUT = 'START_LOGOUT';
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'LOGOUT_ERROR';
export const START_PASSWORD_RESET = 'START_PASSWORD_RESET';
export const PASSWORD_RESET_SUCCESS = 'PASSWORD_RESET_SUCCESS';
export const PASSWORD_RESET_ERROR = 'PASSWORD_RESET_ERROR';
export const START_SETTING_NEW_PASSWORD = 'START_SETTING_NEW_PASSWORD';
export const NEW_PASSWORD_SETTING_SUCCESS = 'NEW_PASSWORD_SETTING_SUCCESS';
export const PASSWORD_MISSING_SYMBOLS = 'PASSWORD_MISSING_SYMBOLS';
export const PASSWORD_MISSING_LETTERS = 'PASSWORD_MISSING_LETTERS';
export const PASSWORD_MISSING_NUMBERS = 'PASSWORD_MISSING_NUMBERS';
export const PASSWORD_MISSING_MIXEDCASE = 'PASSWORD_MISSING_MIXEDCASE';
export const PASSWORD_COMPROMISED = 'PASSWORD_COMPROMISED';
export const NEW_PASSWORD_SETTING_ERROR = 'NEW_PASSWORD_SETTING_ERROR';
export const START_GIVING_PRIVACY_CONSENT = 'START_GIVING_PRIVACY_CONSENT';
export const GIVING_PRIVACY_CONSENT_SUCCESS = 'GIVING_PRIVACY_CONSENT_SUCCESS';
export const GIVING_PRIVACY_CONSENT_ERROR = 'GIVING_PRIVACY_CONSENT_ERROR';
export const PASSWORD_VALIDATION_ERROR = 'PASSWORD_VALIDATION_ERROR';

export function startLogin(username) {
  return {
    type: START_LOGIN,
    username,
  };
}

export function loginSuccess(data = {}) {
  return {
    type: LOGIN_SUCCESS,
    data,
  };
}

export function successfulLogin(data = {}) {
  return function (dispatch) {
    dispatch(loginSuccess(data));
  };
}

export function showPrivacy() {
  return function (dispatch) {
    dispatch(push(PRIVACY));
  };
}

export const failedLogin = createError(LOGIN_ERROR);

export const invalidCredentialsError = createError(INVALID_CREDENTIALS_ERROR);

export const noActiveCampaignError = createError(NO_ACTIVE_CAMPAIGN_ERROR);

export const authExpiredError = createError(AUTH_EXPIRED_ERROR);

async function handleLogin(fetch, getState, dispatch) {
  const {data: user} = await fetch(`${config.api}/auth/me`);
  dispatch(successfulLogin(user));
  local.set('feUser', user);

  const {data: campaigns} = await fetch(`${config.api}/usercampaign/list`);

  if (!campaigns || campaigns.length < 1) {
    dispatch(logout());
    dispatch(noActiveCampaignError());
    return;
  }

  dispatch(setUserCampaignList(campaigns));

  if (campaigns.length === 1) {
    const firstCampaign = campaigns[0];
    const userLang = firstCampaign.user.lang;
    const campaignLangs = firstCampaign.languages;

    const canSkipLangSelection = campaignLangs.length === 1 || Boolean(userLang);
    if (canSkipLangSelection) {
      dispatch(selectUserCampaign(firstCampaign.id, userLang ? null : campaignLangs[0].lang)).then(
        resp => {
          const {privacy, privacyAgreement, campaign} = resp;

          const state = getState();
          const isAssessor = state.getIn(['user', 'data', 'role', 'name']) === 'Assessor';

          const correctLangLabels = campaign.languages.find(label => label.lang === userLang) || {};
          const newCampaign = {...campaign, ...correctLangLabels};
          dispatch(loadUserCampaignsSuccess(Immutable.fromJS(newCampaign)));
          dispatch(isAssessor ? setThemeId(THEMES.BASIC) : setThemeId(firstCampaign.theme));

          if (privacyAgreement) {
            dispatch(privacyAgreementLoadingSuccess(privacyAgreement));
          }

          if (!privacy) {
            dispatch(push(PRIVACY));
            return;
          }

          const fromLocation = state.getIn(['router', 'location', 'state', 'from', 'pathname']);
          if (fromLocation) {
            dispatch(push(fromLocation !== LOGIN ? fromLocation : HOME));
          }

          dispatch(push(HOME));
        }
      );
      return;
    }
  }

  dispatch(push(CAMPAIGNSELECTOR));
}

export function login(username, password, timezone = '') {
  return async function (dispatch, getState) {
    const fetch = buildFetch(dispatch);

    dispatch(startLogin(username));

    try {
      const loginResponse = await fetch(`${config.api}/auth/login`, {
        method: 'POST',
        data: {
          username,
          password,
          timezone,
        },
      });
      const {token, email} = loginResponse.data;

      if (token && email) {
        dispatch(push(getParametricRoute(NEW_PASSWORD, {token, email})));
        return;
      }

      await handleLogin(fetch, getState, dispatch);
    } catch (error) {
      if (error) {
        const {response} = error;

        if (!response) {
          console.error(error);
          return;
        }

        const {data} = response;

        if (data.error === 422 && data.code === 'WRONG_CREDENTIALS') {
          dispatch(invalidCredentialsError());
        } else if (data.error === 422 && data.code === 'NO_ACTIVE_CAMPAIGN') {
          dispatch(noActiveCampaignError());
          dispatch(push({pathname: LOGIN, state: {username}}));
        } else {
          dispatch(failedLogin());
        }
      }
    }
  };
}

export function refreshLogin() {
  return async function (dispatch, getState) {
    const fetch = buildFetch(dispatch);

    dispatch(startLogin());

    try {
      await dispatch(loadLanguages());
      await handleLogin(fetch, getState, dispatch);
    } catch (err) {
      console.log({err});
    }
  };
}

export function startLogout() {
  return {
    type: START_LOGOUT,
  };
}

export function successfulLogout() {
  return {
    type: LOGOUT_SUCCESS,
  };
}

export const failedLogout = createError(LOGOUT_ERROR);

function doLogout(dispatch, state) {
  dispatch(successfulLogout());

  local.remove('feUser');

  const isComingFromSSO = state.getIn(['user', 'data', 'hasSSO']);
  if (isComingFromSSO) {
    dispatch(push(SSO_LOGOUT));
    return;
  }

  dispatch(push(LOGIN));
}

export function logout() {
  return function (dispatch, getState) {
    dispatch(startLogout());

    fetch(`${config.api}/auth/logout`, {
      method: 'POST',
    })
      .then(response => {
        const logoutData = response.data;
        if (!logoutData.status) {
          throw new Error('Invalid logout');
        }

        doLogout(dispatch, getState());
      })
      .catch(error => {
        // TODO: manually handle offline and timeout
        if (error) {
          if (error.response && error.response.data && error.response.data.error === 401) {
            doLogout(dispatch);
            return;
          }

          const {message} = error;
          dispatch(failedLogout(message));
        }
      });
  };
}

export function unauthorized() {
  return function (dispatch, getState) {
    if (isAuthenticated(getState())) {
      dispatch(authExpiredError());
    }

    dispatch(push(LOGIN));
  };
}

const sendPasswordResetError = createError(PASSWORD_RESET_ERROR);

export function sendPasswordReset(email) {
  return dispatch => {
    dispatch({type: START_PASSWORD_RESET});

    const fetch = buildFetch(dispatch);
    fetch(`${config.api}/auth/sendPasswordReset`, {
      method: 'POST',
      data: {
        email,
      },
    })
      .then(() => {
        dispatch({type: PASSWORD_RESET_SUCCESS});
      })
      .catch(error => {
        if (error) {
          dispatch(sendPasswordResetError(error));
        }
      });
  };
}

const newPasswordSettingError = createError(NEW_PASSWORD_SETTING_ERROR);
const passwordValidationSymbols = createError(PASSWORD_MISSING_SYMBOLS);
const passwordValidationLetters = createError(PASSWORD_MISSING_LETTERS);
const passwordValidationNumbers = createError(PASSWORD_MISSING_NUMBERS);
const passwordValidationMixed = createError(PASSWORD_MISSING_MIXEDCASE);
const passwordValidationUncompromised = createError(PASSWORD_COMPROMISED);

export function setNewPassword({password, confirmPassword, email, token, timezone = ''}) {
  return dispatch => {
    dispatch({type: START_SETTING_NEW_PASSWORD});

    const fetch = buildFetch(dispatch);
    fetch(`${config.api}/auth/resetPassword`, {
      method: 'POST',
      data: {
        email,
        token,
        password,
        password_confirmation: confirmPassword,
      },
    })
      .then(response => {
        const {status, username} = response.data;

        if (status !== true) {
          throw new Error('Invalid password setting data');
        }

        dispatch({type: NEW_PASSWORD_SETTING_SUCCESS});

        if (username) {
          dispatch(startLogin(username));

          fetch(`${config.api}/auth/login`, {
            method: 'POST',
            data: {
              username,
              password,
              timezone,
            },
          })
            .then(response => {
              const {token, email, campaigns} = response.data;
              // ==  if token and email, reset password  ==
              if (token && email) {
                dispatch(push(getParametricRoute(NEW_PASSWORD, {token, email})));
                return;
              }

              const loginData = response.data.user;

              if (!loginData || !Number.isInteger(loginData.id)) {
                throw new Error('Invalid login data');
              }

              dispatch(successfulLogin(loginData));
              local.set('feUser', loginData);

              if (!campaigns || campaigns.length < 1) {
                dispatch(logout());
                dispatch(noActiveCampaignError());

                return;
              }

              dispatch(setUserCampaignList(campaigns));
              dispatch(push(CAMPAIGNSELECTOR));
            })
            .catch(error => {
              // RETRY
              dispatch(login(username, password));
            });

          return;
        }

        dispatch(push(LOGIN));
      })
      .catch(error => {
        if (error) {
          const {response} = error;

          if (
            response.data &&
            response.data.errors &&
            response.data.errors.password &&
            response.data.errors.password.length > 0
          ) {
            switch (response.data.errors.password[0]) {
              case 'validation.password.symbols':
                dispatch(passwordValidationSymbols());
                return;
              case 'validation.password.letters':
                dispatch(passwordValidationLetters());
                return;
              case 'validation.password.numbers':
                dispatch(passwordValidationNumbers());
                return;
              case 'validation.password.mixed':
                dispatch(passwordValidationMixed());
                return;
              case 'validation.password.uncompromised':
                dispatch(passwordValidationUncompromised());
                return;
            }
          }

          dispatch(newPasswordSettingError(error));
        }
      });
  };
}

const givingPrivacyConsentError = createError(GIVING_PRIVACY_CONSENT_ERROR);

export function givePrivacyConsent() {
  return (dispatch, getState) => {
    dispatch({type: START_GIVING_PRIVACY_CONSENT});

    const fetch = buildFetch(dispatch);
    fetch(`${config.api}/auth/privacy/giveConsent`, {
      method: 'POST',
    })
      .then(response => {
        if (response.data.status !== true) {
          throw new Error('Invalid privacy consent setting data');
        }

        dispatch({type: GIVING_PRIVACY_CONSENT_SUCCESS});
        const state = getState();
        local.set('feUser', getUserData(state));

        const fromLocation = getFromLocation(state);
        dispatch(push(fromLocation && fromLocation !== LOGIN ? fromLocation : HOME));
      })
      .catch(error => {
        if (error) {
          dispatch(givingPrivacyConsentError(error));
        }
      });
  };
}

export const passwordValidationError = createError(PASSWORD_VALIDATION_ERROR);
